import type { HTMLAttributes, RefObject } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@nexelem/utils';

const nodeVariants = cva(
  'relative box-border flex h-full min-w-11 shrink-0 cursor-pointer touch-none items-center overflow-hidden rounded-full border border-border-primary-darker bg-surface-container px-1 transition-all ease-in-out group-hover:border-border-hover-lighter group-hover:bg-surface-hover-lighter',
  {
    variants: {
      unassigned: {
        true: 'border-border-state-warning-dark bg-surface-primary hover:border-border-state-warning-dark',
      },
      isDragged: {
        true: 'max-w-64 border-border-focus bg-surface-focus-lighter',
      },
      isDisabled: {
        true: 'border-border-disabled bg-surface-disabled text-text-disabled',
      },
      isSelected: {
        true: 'border-border-focus bg-surface-focus-lighter! group-hover:border-border-focus hover:border-border-focus',
      },
      isSquared: {
        true: 'rounded-sm!',
      },
      toPublish: {
        false:
          'border-2 border-dashed border-border-primary-darker bg-surface-primary group-hover:border-border-primary-darker hover:border-border-primary-darker',
      },
      isOverdue: {
        true: 'border-2 border-border-status-rose group-hover:border-border-status-rose hover:border-border-status-rose',
      },
      warehouseShortage: {
        true: 'border-border-status-yellow-lighter group-hover:border-border-status-yellow-lighter hover:border-border-status-yellow-lighter',
      },
      hasError: {
        true: 'border-2 border-border-state-error-dark group-hover:border-border-state-error-dark hover:border-border-state-error-dark',
      },
      isGraph: {
        true: 'cursor-default p-0',
      },
      isDragging: {
        true: 'opacity-50',
        false: 'opacity-100',
      },
      isEmphasized: {
        true: 'border-4 border-icon-dark bg-surface-status-node-search group-hover:border-icon-dark hover:border-icon-dark',
      },
      isGraphHighlight: {
        true: 'border-4 border-icon-dark group-hover:border-icon-dark hover:border-icon-dark',
      },
      isSearchSelected: {
        true: 'animate-node-pulse',
      },
      isSelectable: {
        false: 'group-hover:bg-surface-container hover:bg-surface-container active:bg-surface-container',
        true: '',
      },
      isSmall: {
        true: 'pt-1.5 pr-4 pb-1.5',
      },
      isRetooling: {
        true: 'rounded-l-none',
      },
      draggable: {
        true: 'cursor-grab',
      },
      multiple: {
        default: 'h-9.5',
        break: 'h-9.5',
      },
    },
    compoundVariants: [
      {
        isRetooling: true,
        isSmall: false,
      },
    ],
  },
);

export type ShadowParams = {
  color: string;
  isPreviousProductEqual: boolean;
  isNextProductEqual: boolean;
};

export type StyledNodeBaseProps = VariantProps<typeof nodeVariants> &
  HTMLAttributes<HTMLDivElement> & {
    width: number;
    shadowParams?: ShadowParams;
    ref?: RefObject<HTMLDivElement>;
  };

export const StyledNodeBase = ({
  width,
  unassigned,
  hasError,
  isDragging,
  isEmphasized,
  isGraph,
  isGraphHighlight,
  isDragged,
  draggable,
  isSmall,
  isRetooling,
  isDisabled,
  isSelected,
  isSelectable,
  isSearchSelected,
  isOverdue,
  isSquared,
  className,
  shadowParams,
  toPublish,
  warehouseShortage,
  ref,
  style,
  multiple,
  onClick,
  onMouseDown,
  onKeyDown,
  onPointerDown,
  ...props
}: StyledNodeBaseProps) => (
  <div
    className={cn('group relative h-11', multiple && 'flex items-end')}
    ref={ref}
    style={{ width }}
    onClick={onClick}
    onKeyDown={onKeyDown}
    onMouseDown={onMouseDown}
    onPointerDown={onPointerDown}
  >
    {multiple && (
      <div
        style={{ width: width - 6 }}
        className={cn(
          'absolute ml-[6px] h-9.5 self-start rounded-[22px] border border-border-primary-darker bg-surface-container',
          'cursor-pointer transition-all ease-in-out group-hover:border-border-hover-lighter group-hover:bg-surface-hover-lighter',
          multiple === 'break' &&
            'rounded-xs bg-surface-break group-hover:border-border-primary-darker group-hover:bg-surface-break-hover',
        )}
      />
    )}
    {shadowParams && (
      <div
        style={{ backgroundColor: shadowParams.color }}
        className={cn(
          'absolute -bottom-[4px] h-6 w-full',
          !shadowParams.isPreviousProductEqual && 'rounded-bl-full',
          !shadowParams.isNextProductEqual && 'rounded-br-full',
          isDragging && 'opacity-0',
        )}
      />
    )}
    <div
      style={{ width: multiple ? width - 6 : width, ...style }}
      className={cn(
        nodeVariants({
          isOverdue,
          isSquared,
          unassigned,
          hasError,
          isDragging,
          isEmphasized,
          isGraph,
          isGraphHighlight,
          isDisabled,
          isDragged,
          isSmall,
          isRetooling,
          draggable,
          isSelected,
          isSelectable,
          isSearchSelected,
          toPublish,
          warehouseShortage,
          multiple,
        }),
        className,
      )}
      {...props}
    />
  </div>
);

StyledNodeBase.displayName = 'StyledNodeBase';
